import React, { useState, useEffect, useCallback } from 'react';
import {
  DEFAULT_MRP_RATING_SETTINGS,
  normalizeMrpRatingSettings,
  applyMrpRatingSettings,
} from '../utils/mrpRatingSettings';
import { MRP_METRICS } from '../utils/mrpStatisticsMetrics';
import { SHIFTS_AND_ONLINE_ENABLED } from '../constants/featureFlags';

const getToken = () => localStorage.getItem('token');

function roundRatingPoints(value) {
  return Math.round((Number(value) || 0) * 10) / 10;
}

function editableRatingValue(val) {
  if (val === '' || val === '.' || val === null || val === undefined) return val === '.' ? '.' : '';
  if (typeof val === 'string') return val;
  if (val === 0) return '';
  return String(val);
}

function SettingsToggle({ label, description, checked, onChange }) {
  return (
    <label className="mrp-rating-toggle">
      <input type="checkbox" className="mrp-rating-toggle-input" checked={checked} onChange={onChange} />
      <span className="mrp-rating-toggle-track" aria-hidden="true">
        <span className="mrp-rating-toggle-thumb" />
      </span>
      <span className="mrp-rating-toggle-text">
        <span className="mrp-rating-toggle-label">{label}</span>
        {description ? <span className="mrp-rating-toggle-desc">{description}</span> : null}
      </span>
    </label>
  );
}

function FormulaInputs({ every, points, everyLabel, pointsPrefix, onEveryChange, onPointsChange, onEveryBlur, onPointsBlur, everyAria, pointsAria }) {
  return (
    <span className="settings-formula">
      <span className="settings-formula__word">{everyLabel}</span>
      <input
        type="text"
        inputMode="numeric"
        placeholder="0"
        className="settings-formula__input"
        value={editableRatingValue(every)}
        onChange={onEveryChange}
        onFocus={(e) => e.target.select()}
        onBlur={onEveryBlur}
        aria-label={everyAria}
      />
      <span className="settings-formula__word">=</span>
      <span className={`settings-formula__sign${pointsPrefix === '−' ? ' settings-formula__sign--minus' : ''}`}>
        {pointsPrefix}
      </span>
      <input
        type="text"
        inputMode="decimal"
        placeholder="0"
        className="settings-formula__input"
        value={editableRatingValue(points)}
        onChange={onPointsChange}
        onFocus={(e) => e.target.select()}
        onBlur={onPointsBlur}
        aria-label={pointsAria}
      />
    </span>
  );
}

export default function SettingsMrpRating({ isActive, setError, setSuccess }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(() => ({ ...DEFAULT_MRP_RATING_SETTINGS }));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/app-settings', {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Не удалось загрузить настройки рейтинга');
      setForm(
        data.mrp_rating
          ? normalizeMrpRatingSettings(data.mrp_rating)
          : { ...DEFAULT_MRP_RATING_SETTINGS },
      );
    } catch (e) {
      setError(e.message);
      setTimeout(() => setError(''), 4000);
    } finally {
      setLoading(false);
    }
  }, [setError]);

  useEffect(() => {
    if (isActive) load();
  }, [isActive, load]);

  const setField = (field, value) => {
    if (typeof value === 'boolean') {
      setForm((prev) => ({ ...prev, [field]: value }));
      return;
    }

    const intFields = new Set(['afk_per_block', 'rating_max']);
    const floatFields = new Set(['afk_penalty', 'reprimand_penalty']);

    if (intFields.has(field)) {
      const clean = String(value).replace(/[^\d]/g, '');
      setForm((prev) => ({
        ...prev,
        [field]: clean === '' ? '' : Math.max(0, parseInt(clean, 10) || 0),
      }));
      return;
    }

    if (floatFields.has(field)) {
      let clean = String(value).replace(',', '.').replace(/[^\d.]/g, '');
      const dot = clean.indexOf('.');
      if (dot !== -1) {
        clean = `${clean.slice(0, dot + 1)}${clean.slice(dot + 1).replace(/\./g, '')}`;
      }
      setForm((prev) => ({ ...prev, [field]: clean }));
      return;
    }

    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const blurField = (field, fallback = 0) => {
    setForm((prev) => {
      const raw = prev[field];
      if (raw === '' || raw === '.' || raw == null) {
        return { ...prev, [field]: fallback };
      }
      if (field === 'reprimand_penalty' || field === 'afk_penalty') {
        return { ...prev, [field]: roundRatingPoints(Math.max(0, parseFloat(raw) || fallback)) };
      }
      return { ...prev, [field]: Math.max(0, parseInt(raw, 10) || fallback) };
    });
  };

  const setMetricRate = (metricKey, field, value) => {
    setForm((prev) => {
      const rates = { ...(prev.metric_rates || {}) };
      const row = { ...(rates[metricKey] || { every: 0, points: 0 }) };
      if (field === 'every') {
        const clean = String(value).replace(/[^\d]/g, '');
        row.every = clean === '' ? '' : Math.max(0, parseInt(clean, 10) || 0);
      } else {
        let clean = String(value).replace(',', '.').replace(/[^\d.]/g, '');
        const dot = clean.indexOf('.');
        if (dot !== -1) {
          clean = `${clean.slice(0, dot + 1)}${clean.slice(dot + 1).replace(/\./g, '')}`;
        }
        row.points = clean;
      }
      rates[metricKey] = row;
      return { ...prev, metric_rates: rates };
    });
  };

  const blurMetricRate = (metricKey, field) => {
    setForm((prev) => {
      const rates = { ...(prev.metric_rates || {}) };
      const row = { ...(rates[metricKey] || { every: 0, points: 0 }) };
      if (field === 'every') {
        row.every = Math.max(0, parseInt(row.every, 10) || 0);
      } else {
        row.points = roundRatingPoints(parseFloat(row.points) || 0);
      }
      rates[metricKey] = row;
      return { ...prev, metric_rates: rates };
    });
  };

  const save = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      const normalized = normalizeMrpRatingSettings(form);
      const res = await fetch('/api/app-settings/mrp_rating', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({ setting_value: normalized }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Не удалось сохранить');
      setForm(normalizeMrpRatingSettings(data.setting_value || normalized));
      applyMrpRatingSettings(normalized);
      setSuccess('Сохранено');
      setTimeout(() => setSuccess(''), 1800);
    } catch (err) {
      setError(err.message);
      setTimeout(() => setError(''), 4000);
    } finally {
      setSaving(false);
    }
  };

  if (!isActive) return null;
  if (loading) return <div className="loading">Загрузка…</div>;

  return (
    <form className="settings-panel" onSubmit={save}>
      <section className="settings-card">
        <h2 className="settings-card__title" title="Сколько раз «каждые N» уложилось в факт за период">
          Баллы за действия
        </h2>
        <div className="settings-rows settings-rows--formula">
          {MRP_METRICS.map((metric) => {
            const rate = form.metric_rates?.[metric.key] || { every: 0, points: 0 };
            return (
              <label key={metric.key} className="settings-row settings-row--formula">
                <span className="settings-row__label">{metric.label}</span>
                <FormulaInputs
                  every={rate.every}
                  points={rate.points}
                  everyLabel="каждые"
                  pointsPrefix="+"
                  onEveryChange={(e) => setMetricRate(metric.key, 'every', e.target.value)}
                  onPointsChange={(e) => setMetricRate(metric.key, 'points', e.target.value)}
                  onEveryBlur={() => blurMetricRate(metric.key, 'every')}
                  onPointsBlur={() => blurMetricRate(metric.key, 'points')}
                  everyAria={`${metric.label}: каждые`}
                  pointsAria={`${metric.label}: баллы`}
                />
              </label>
            );
          })}
        </div>
      </section>

      <section className="settings-card">
        <h2 className="settings-card__title">Штрафы</h2>
        <div className="settings-rows settings-rows--formula">
          <label className="settings-row settings-row--formula">
            <span className="settings-row__label">Выговор</span>
            <span className="settings-formula">
              <span className="settings-formula__word">каждый =</span>
              <span className="settings-formula__sign settings-formula__sign--minus">−</span>
              <input
                type="text"
                inputMode="decimal"
                placeholder="0"
                className="settings-formula__input"
                value={editableRatingValue(form.reprimand_penalty)}
                onChange={(e) => setField('reprimand_penalty', e.target.value)}
                onFocus={(e) => e.target.select()}
                onBlur={() => blurField('reprimand_penalty', 0)}
                aria-label="Штраф за выговор"
              />
            </span>
          </label>
          {SHIFTS_AND_ONLINE_ENABLED && (
            <label className="settings-row settings-row--formula">
              <span className="settings-row__label">AFK</span>
              <FormulaInputs
                every={form.afk_per_block}
                points={form.afk_penalty}
                everyLabel="каждые"
                pointsPrefix="−"
                onEveryChange={(e) => setField('afk_per_block', e.target.value)}
                onPointsChange={(e) => setField('afk_penalty', e.target.value)}
                onEveryBlur={() => blurField('afk_per_block', 1)}
                onPointsBlur={() => blurField('afk_penalty', 0)}
                everyAria="AFK: каждые слётов"
                pointsAria="AFK: штраф"
              />
            </label>
          )}
        </div>
      </section>

      <section className="settings-card">
        <h2 className="settings-card__title">Лимит</h2>
        <div className="settings-rows">
          <label className="settings-row" title="Максимальный рейтинг после всех надбавок и штрафов">
            <span className="settings-row__label">Потолок</span>
            <input
              type="text"
              inputMode="numeric"
              className="settings-row__input"
              placeholder="5"
              value={editableRatingValue(form.rating_max)}
              onChange={(e) => setField('rating_max', e.target.value)}
              onFocus={(e) => e.target.select()}
              onBlur={() => blurField('rating_max', 5)}
              aria-label="Максимальный рейтинг"
            />
          </label>
        </div>
      </section>

      <section className="settings-card">
        <h2 className="settings-card__title">Уведомления</h2>
        <div className="settings-rows settings-rows--toggles">
          <SettingsToggle
            label="Telegram при изменении рейтинга"
            checked={form.notify_telegram_on_change !== false}
            onChange={(e) => setField('notify_telegram_on_change', e.target.checked)}
          />
        </div>
      </section>

      <div className="settings-actions">
        <button type="button" className="settings-btn settings-btn--ghost" onClick={load} disabled={saving}>
          Отменить
        </button>
        <button type="submit" className="settings-btn settings-btn--primary" disabled={saving}>
          {saving ? '…' : 'Сохранить'}
        </button>
      </div>
    </form>
  );
}
